import * as React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHandshake, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { TextField } from "office-ui-fabric-react/lib/TextField";
import { Checkbox } from "office-ui-fabric-react/lib/Checkbox";
import { PrimaryButton, DefaultButton } from "office-ui-fabric-react";
import { MessageBar, MessageBarType } from "office-ui-fabric-react";
import api from "../../api";
import { microsoftAuth } from "../../../utils/microsoft-auth.service";
import { MOCK_BASE_URL } from "../../../mock/mockInterceptor";
import AppContext from "../AppContext";
import ProgressOverlay from "../GrayOverlay";
import "./Login.css";

type LoginProps = {};
type LoginState = {
  step: 'config' | 'auth';
  isLoading: boolean;
  baseURL: string;
  urlError: string;
  apiKey: string;
  microsoftAuthSupported: boolean;
  authAttempted: boolean;
  authMethod: 'microsoft' | 'api-key' | null;
  error: string | null;
  enableLogOnSend: boolean;
};

class Login extends React.Component<LoginProps, LoginState> {
  static contextType = AppContext;

  constructor(props: LoginProps) {
    super(props);
    
    const storedBaseURL = localStorage.getItem("baseURL") || "";
    const hasStoredBaseURL = storedBaseURL.trim().length > 0;
    const storedEnableLogOnSend = localStorage.getItem("enable_log_on_send") === "true";

    this.state = {
      step: hasStoredBaseURL ? 'auth' : 'config',
      isLoading: hasStoredBaseURL,
      baseURL: storedBaseURL,
      urlError: "",
      apiKey: "",
      microsoftAuthSupported: microsoftAuth.isSupported(),
      authAttempted: false,
      authMethod: null,
      error: null,
      enableLogOnSend: storedEnableLogOnSend
    };
  }

  componentDidMount() {
    if (this.state.baseURL.trim()) {
      this.handleAutomaticAuth();
    }
  }

  private handleConfigSubmit = (): void => {
    const { baseURL, enableLogOnSend } = this.state;

    if (!baseURL.trim()) {
      this.setState({ urlError: "Please enter your database URL" });
      return;
    }

    try {
      const sanitizedURL = this.sanitizeDbUrl(baseURL);
      localStorage.setItem("baseURL", sanitizedURL);
      const runtimeAvailable = this.isOnSendRuntimeAvailable();
      const finalEnableLogOnSend = runtimeAvailable ? enableLogOnSend : false;
      localStorage.setItem("enable_log_on_send", finalEnableLogOnSend ? "true" : "false");
      if (this.context?.setEnableLogOnSend) {
        this.context.setEnableLogOnSend(finalEnableLogOnSend);
      }

      this.setState({
        step: 'auth',
        baseURL: sanitizedURL,
        urlError: "",
        isLoading: true,
        error: null,
        authAttempted: false
      }, () => {
        this.handleAutomaticAuth();
      });
    } catch (err) {
      this.setState({
        urlError: "Invalid database URL. Please use format: https://mycompany.odoo.com"
      });
    }
  };

  private handleAutomaticAuth = async (): Promise<void> => {
    const { baseURL, microsoftAuthSupported } = this.state;

    if (!baseURL.trim()) {
      this.setState({ 
        isLoading: false,
        error: "Please enter your Odoo database URL to continue"
      });
      return;
    }

    // Demo / mock mode: auto-connect after a realistic-looking delay.
    if (baseURL.trim() === MOCK_BASE_URL) {
      this.setState({ isLoading: true });
      setTimeout(() => {
        const demoToken = "demo-mock-token";
        localStorage.setItem("odooConnectionToken", demoToken);
        localStorage.setItem("authMethod", "api-key");
        api.baseURL = MOCK_BASE_URL;
        if (this.context?.connect && this.context?.navigation?.goToMain) {
          this.context.connect(demoToken);
          this.context.navigation.goToMain();
        }
      }, 1500);
      return;
    }

    this.setState({
      isLoading: true,
      authMethod: 'api-key',
      error: null,
      authAttempted: false
    });

    try {
      const authHeader = localStorage.getItem("odooConnectionToken");
      
      if (authHeader) {
        console.log('Found saved API key, attempting connection...');
        api.baseURL = this.sanitizeDbUrl(baseURL);
        localStorage.setItem("baseURL", baseURL);
        
        if (this.context && this.context.connect && this.context.navigation && this.context.navigation.goToMain) {
          this.context.connect(authHeader);
          this.context.navigation.goToMain();
        }
        return;
      }
    } catch (error) {
      console.log("API key auth failed:", error);
    }

    if (microsoftAuthSupported) {
      try {
        this.setState({ authMethod: 'microsoft' });
        const result = await microsoftAuth.authenticate(baseURL);

        if (result.success && result.token) {
          localStorage.setItem("microsoftToken", result.token);
          if (result.userInfo) {
            localStorage.setItem("microsoftUserInfo", JSON.stringify(result.userInfo));
          }
          
          if (this.context && this.context.connectWithMicrosoft && this.context.navigation && this.context.navigation.goToMain) {
            this.context.connectWithMicrosoft(result.token, result.userInfo);
            this.context.navigation.goToMain();
          }
          return;
        }
      } catch (error) {
        console.log("Microsoft SSO failed:", error);
      }
    }
    // Both methods failed, show manual login form
    this.setState({ 
      isLoading: false,
      authAttempted: true,
      authMethod: null,
      error: "Please enter your API key to connect to Odoo"
    });
  };

  private handleManualLogin = (): void => {
    const { baseURL, apiKey } = this.state;
    
    if (!baseURL.trim()) {
      this.setState({ urlError: "Please enter your database URL" });
      return;
    }

    if (!apiKey.trim()) {
      this.setState({ urlError: "Please enter your API key" });
      return;
    }

    try {
      const sanitizedURL = this.sanitizeDbUrl(baseURL);
      const authHeader = `Bearer ${apiKey}`;

      api.baseURL = sanitizedURL;
      localStorage.setItem("baseURL", sanitizedURL);
      localStorage.setItem("odooConnectionToken", authHeader);
      localStorage.setItem("authMethod", "api-key");
      
      if (this.context && this.context.connect && this.context.navigation && this.context.navigation.goToMain) {
        this.context.connect(authHeader);
        this.context.navigation.goToMain();
      }
    } catch (err) {
      this.setState({
        urlError: "Invalid database URL. Please use format: https://mycompany.odoo.com"
      });
    }
  };

  private sanitizeDbUrl = (urlStr: string): string => {
    let tmpStr = urlStr.trim();
    
    if (!tmpStr.startsWith("http://") && !tmpStr.startsWith("https://")) {
      tmpStr = "https://" + tmpStr;
    }
    
    const url = new URL(tmpStr);
    url.protocol = "https:";
    return url.origin;
  };

  private onServerChange = (_, newValue?: string): void => {
    this.setState({
      baseURL: newValue || "",
      urlError: ""
    });
  };

  private onApiKeyChange = (_, newValue?: string): void => {
    this.setState({ apiKey: newValue || "" });
  };

  private onEnableLogOnSendChange = (_, checked?: boolean): void => {
    this.setState({ enableLogOnSend: !!checked });
  };

  private isOnSendRuntimeAvailable = (): boolean => {
    try {
      // OWA and New Outlook run in a WebView and support event-based add-ins.
      const hostName = Office?.context?.mailbox?.diagnostics?.hostName;
      if (hostName === "OutlookWebApp" || hostName === "newOutlook" || hostName === "newOutlookWindows") {
        return true;
      }
      // Fallback: the event runtime wrote a marker once loaded.
      const roamingReady = Office?.context?.roamingSettings?.get("onSendRuntimeReady");
      if (typeof roamingReady === "string" && roamingReady.length > 0) {
        return true;
      }
      return false;
    } catch (e) {
      return false;
    }
  };

  private onRetryAuth = (): void => {
    this.setState({ 
      isLoading: true,
      error: null,
      authAttempted: false
    });
    this.handleAutomaticAuth();
  };

  render() {
    const {
      step,
      isLoading,
      baseURL,
      urlError,
      apiKey,
      error,
      enableLogOnSend
    } = this.state;

    const runtimeAvailable = this.isOnSendRuntimeAvailable();

    // Step 1: Database Configuration
    if (step === 'config') {
      return (
        <>
          <div className="lower-bounded-tile">
            <span className="link-like-button" onClick={() => {
              if (this.context && this.context.navigation && this.context.navigation.goToMain) {
                this.context.navigation.goToMain();
              }
            }}>
              &larr; BACK
            </span>
          </div>

          <div className="bounded-tile">
            <div style={{ fontSize: "30px", width: "200px", margin: "auto", textAlign: "center" }}>
              <FontAwesomeIcon icon={faHandshake} />
            </div>
            <h1 style={{ textAlign: "center", marginBottom: "30px" }}>Configure Odoo Connection</h1>
            
            <p style={{ textAlign: "center", marginBottom: "20px", color: "#666" }}>
              Enter your Odoo database URL to get started
            </p>

            <TextField
              label="Database URL"
              value={baseURL}
              onChange={this.onServerChange}
              placeholder="https://mycompany.odoo.com"
              errorMessage={urlError}
              styles={{ root: { marginBottom: "24px" } }}
            />

            <Checkbox
              label="Enable logging on send (O365 admin approval needed)"
              checked={enableLogOnSend && runtimeAvailable}
              onChange={this.onEnableLogOnSendChange}
              disabled={!runtimeAvailable}
              styles={{ root: { marginBottom: "24px" } }}
            />

            {!runtimeAvailable && (
              <p style={{ fontSize: "12px", color: "#a80000", marginBottom: "16px" }}>
                Automatic logging on send requires Outlook with WebView support (New Outlook or Outlook on the web).
              </p>
            )}

            <PrimaryButton
              text="Continue"
              onClick={this.handleConfigSubmit}
              disabled={isLoading}
              iconProps={{ iconName: "Forward" }}
              style={{ width: "100%" }}
            />
          </div>
        </>
      );
    }

    // Step 2: Authentication
    // Show loading state during automatic authentication
    if (isLoading) {
      return (
        <div style={{ position: 'relative', minHeight: '180px' }}>
          <ProgressOverlay />
        </div>
      );
    }

    // Show manual authentication form if automatic auth failed
    return (
      <>
        <div className="lower-bounded-tile">
          <span className="link-like-button" onClick={() => {
            this.setState({ step: 'config' });
          }}>
            &larr; CHANGE SERVER
          </span>
        </div>

        <div className="bounded-tile">
          <div style={{ fontSize: "30px", width: "200px", margin: "auto", textAlign: "center" }}>
            <FontAwesomeIcon icon={faHandshake} />
          </div>
          <h1 style={{ textAlign: "center", marginBottom: "30px" }}>Authenticate with Odoo</h1>
          
          <p style={{ textAlign: "center", marginBottom: "20px", color: "#666" }}>
            Connected to: <strong>{baseURL}</strong>
          </p>

          {this.context?.loginErrorMessage && (
            <MessageBar messageBarType={MessageBarType.error} styles={{ root: { marginBottom: "16px" } }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <span>{this.context.loginErrorMessage}</span>
              </div>
            </MessageBar>
          )}

          {error && (
            <MessageBar messageBarType={MessageBarType.warning}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <span>{error}</span>
              </div>
            </MessageBar>
          )}

          <TextField
            label="API Key"
            value={apiKey}
            onChange={this.onApiKeyChange}
            placeholder="Your Odoo API key"
            styles={{ root: { marginBottom: "24px" } }}
          />

          <PrimaryButton
            text="Connect"
            onClick={this.handleManualLogin}
            disabled={isLoading}
            iconProps={{ iconName: "SignIn" }}
            style={{ width: "100%" }}
          />

          <DefaultButton
            text="Try Automatic Authentication Again"
            onClick={this.onRetryAuth}
            style={{ width: "100%", marginTop: "8px" }}
          />

          <DefaultButton
            text="Test Microsoft SSO Only"
            onClick={async () => {
              console.log('🧪 Testing Microsoft SSO only...');
              try {
                const result = await microsoftAuth.testSSOToken();
                console.log('Test result:', result);
                alert(`Microsoft SSO Test: ${result.success ? 'SUCCESS' : 'FAILED'}\n${result.error || 'Token retrieved successfully!'}`);
              } catch (error) {
                console.error('Test failed:', error);
                alert(`Microsoft SSO Test: FAILED\n${error.message}`);
              }
            }}
            style={{ width: "100%", marginTop: "8px" }}
          />
        </div>
      </>
    );
  }
}

export default Login;